/**
 * Участники, приглашения, клиенты агентства.
 *
 * Тонкость, из-за которой эти вызовы стоят рядом: клиент агентства — это не
 * отдельная сущность, а **роль членства, привязанная к одному пространству**
 * (спека §5). Поэтому пригласить клиента и пригласить аналитика — один и тот же
 * эндпоинт, а различает их `workspace_id`: для роли «клиент агентства» он
 * обязателен, для владельца запрещён.
 *
 * Приглашение и смена роли требуют подтверждения на действии, поэтому оба
 * принимают токен step-up. Ключ идемпотентности передаётся снаружи: при повторе
 * после подтверждения он обязан быть тем же, иначе на почту уйдёт второе письмо.
 */

import { api, type Page } from './client';
import { ApiError } from './problem';
import type { components } from './schema';

export type Member = components['schemas']['Member'];
export type MemberRole = components['schemas']['MemberRole'];
export type MemberUpdate = components['schemas']['MemberUpdate'];
export type Invitation = components['schemas']['Invitation'];
export type InvitationCreate = components['schemas']['InvitationCreate'];
export type OwnershipTransfer = components['schemas']['OwnershipTransfer'];
export type AgencyClient = components['schemas']['AgencyClient'];
export type Workspace = components['schemas']['Workspace'];
export type WorkspaceAllocation = components['schemas']['WorkspaceAllocation'];
export type OrganizationQuotas = components['schemas']['OrganizationQuotas'];

/**
 * Действующие квоты организации. Отдельно от биллингового портала: портал видит
 * только владелец, а квоты нужны и аналитику — он раздаёт их между клиентами.
 */
export function getOrganizationQuotas(signal?: AbortSignal): Promise<OrganizationQuotas> {
  return api.get<OrganizationQuotas>('/organizations/current/quotas', { signal });
}

/**
 * Все ЖИВЫЕ пространства организации, а не одни клиентские.
 *
 * Нужны там, где считается остаток общей квоты: сервер складывает выделения
 * ВСЕХ пространств, включая внутреннее пространство команды, которого в
 * агентском списке нет.
 *
 * `status: 'active'` — не украшение запроса, а то же правило, по которому
 * считает сервер. Архивное пространство квоту за собой не держит
 * (`pg_workspaces.allocated_total`: «архивное не меряется, и держать за собой
 * вопросы тарифа ему не за что»), но в общем списке остаётся. Без этого условия
 * удалённый клиент пропадал бы из `GET /agency/clients` и оставался здесь —
 * попадал в `spacesOutsideClients`, у него спрашивали выделение, и остаток
 * свободной квоты занижался ровно на долю только что удалённого клиента.
 * Область (`scope.tsx`) спрашивает пространства с тем же условием.
 */
export function listWorkspaces(signal?: AbortSignal): Promise<Page<Workspace>> {
  return api.get<Page<Workspace>>('/workspaces', {
    signal,
    query: { limit: 100, status: 'active' },
  });
}

/**
 * Убрать пространство клиента из кабинета.
 *
 * Ручка называется `archive`, а кнопка — «Удалить клиента», и расхождение
 * намеренное: ленты архивных клиентов в продукте нет, вернуть клиента
 * самостоятельно нельзя, и для покупателя это удаление. Строки при этом не
 * стираются — на них висят прогоны, снимки и отчёты.
 *
 * Ключа идемпотентности нет: операция идемпотентна сама по себе (второй вызов
 * упирается в `409`, а не заводит ничего второго), и контракт заголовка у неё не
 * объявляет.
 *
 * Пространство архивируется ПУСТЫМ: пока в нём есть хоть один неархивный бренд,
 * сервер отвечает `409` и называет их число. Это его текст и его число — кабинет
 * его не переписывает.
 */
export function archiveWorkspace(workspaceId: string): Promise<Workspace> {
  return api.post<Workspace>(`/workspaces/${encodeURIComponent(workspaceId)}/archive`);
}

/**
 * Квота, выделенная одному пространству.
 *
 * `404` — **обычный ответ**: выделения у пространства может не быть вовсе, и
 * это не сбой, а «квота не выделена». Отсюда `null` вместо ошибки.
 */
export function getWorkspaceAllocation(
  workspaceId: string,
  signal?: AbortSignal,
): Promise<WorkspaceAllocation | null> {
  return api
    .get<WorkspaceAllocation>(
      `/workspaces/${encodeURIComponent(workspaceId)}/allocation`,
      { signal },
    )
    .catch((error: unknown) => {
      if (error instanceof ApiError && error.problem.status === 404) return null;
      throw error;
    });
}

export function listMembers(signal?: AbortSignal): Promise<Page<Member>> {
  return api.get<Page<Member>>('/members', { signal, query: { limit: 100 } });
}

export function listInvitations(signal?: AbortSignal): Promise<Page<Invitation>> {
  return api.get<Page<Invitation>>('/invitations', {
    signal,
    query: { limit: 100, state: 'pending' },
  });
}

export function inviteMember(
  body: InvitationCreate,
  key: string,
  stepUpToken?: string,
): Promise<Invitation> {
  return api.post<Invitation>('/invitations', body, {
    idempotencyKey: key,
    stepUpToken,
  });
}

export function resendInvitation(id: string, key: string): Promise<Invitation> {
  return api.post<Invitation>(
    `/invitations/${encodeURIComponent(id)}/resend`,
    undefined,
    { idempotencyKey: key },
  );
}

export function revokeInvitation(id: string): Promise<void> {
  return api.delete<void>(`/invitations/${encodeURIComponent(id)}`);
}

export function updateMember(
  id: string,
  body: MemberUpdate,
  stepUpToken?: string,
): Promise<Member> {
  return api.patch<Member>(`/members/${encodeURIComponent(id)}`, body, { stepUpToken });
}

export function removeMember(id: string, stepUpToken?: string): Promise<void> {
  return api.delete<void>(`/members/${encodeURIComponent(id)}`, { stepUpToken });
}

/* ──────────────────────── передача владения организацией ─────────────────── */

/**
 * Владение передаётся **в два хода**, и это главное, что о нём нужно знать.
 *
 * Владелец начинает передачу, принимающая сторона её подтверждает, и только
 * после подтверждения роли меняются: до тех пор владелец прежний. Иначе одним
 * щелчком можно было бы отдать доступ к деньгам организации человеку, который
 * об этом не просил и, возможно, уже не работает в компании.
 *
 * Отсюда же и права: начать и отменить может только владелец
 * (`/organizations/current/**`), а принять — **любой участник**
 * (`/ownership-transfers/{transferId}/accept` в матрице стоит рядом с
 * `/invitations/accept`). Принимающий на момент подтверждения ещё не владелец,
 * и требовать от него роли владельца значило бы сделать передачу невозможной.
 *
 * Активная передача одна на организацию: вторая упрётся в `409`.
 */

/**
 * Активная передача владения или `null`, если её нет.
 *
 * `404` здесь — **обычный ответ**, а не сбой: передач не бывает почти никогда.
 * Показывать из-за него ошибку на экране участников значило бы пугать человека
 * тем, что всё в порядке.
 */
export function getOwnershipTransfer(
  signal?: AbortSignal,
): Promise<OwnershipTransfer | null> {
  return api
    .get<OwnershipTransfer>('/organizations/current/ownership-transfer', { signal })
    .catch((error: unknown) => {
      if (error instanceof ApiError && error.problem.status === 404) return null;
      throw error;
    });
}

export function startOwnershipTransfer(
  toEmail: string,
  key: string,
  stepUpToken?: string,
): Promise<OwnershipTransfer> {
  return api.post<OwnershipTransfer>(
    '/organizations/current/ownership-transfer',
    { to_email: toEmail },
    { idempotencyKey: key, stepUpToken },
  );
}

export function cancelOwnershipTransfer(): Promise<void> {
  return api.delete<void>('/organizations/current/ownership-transfer');
}

export function acceptOwnershipTransfer(
  transferId: string,
  stepUpToken?: string,
): Promise<OwnershipTransfer> {
  return api.post<OwnershipTransfer>(
    `/ownership-transfers/${encodeURIComponent(transferId)}/accept`,
    undefined,
    { stepUpToken },
  );
}

/* ─────────────────────────────── агентство ───────────────────────────────── */

export function listAgencyClients(signal?: AbortSignal): Promise<Page<AgencyClient>> {
  return api.get<Page<AgencyClient>>('/agency/clients', {
    signal,
    query: { limit: 100 },
  });
}

/**
 * Что кабинет отправляет, заводя клиента.
 *
 * Поле `brand` контракта отрезано намеренно, и это не упрощение формы. Сервер
 * его молча выбрасывает: разбор сайта и сборка семантического ядра принадлежат
 * онбордингу бренда, и второго места, где решается, что такое бренд, в продукте
 * нет. Отправлять поле, зная, что его игнорируют, — это обещание клиенту,
 * которого никто не сдержит: он заполнил адрес сайта и получил пустое
 * пространство. Закрыто типом, а не договорённостью: `Omit` не даст отправить.
 */
export type AgencyClientCreate = Omit<
  components['schemas']['AgencyClientCreate'],
  'brand'
>;

/**
 * Завести клиента: пространство, его доля общей квоты и, если указана почта,
 * приглашение с ролью «клиент агентства».
 *
 * Ключ идемпотентности **снаружи** и обязателен: без заголовка сервер отвечает
 * `422`. Причина держать его снаружи здесь дороже, чем у приглашения. Повтор с
 * новым ключом заводит ВТОРОЕ пространство — оно занимает место в тарифе, тянет
 * за собой второе приглашение на ту же почту, а убрать его из кабинета нечем:
 * удаления пространств в продукте нет. Поэтому ключ создаёт вызывающий, один
 * раз на попытку (`ui/attempt.ts`), и повтор после обрыва уходит с тем же.
 *
 * Подтверждения на действии здесь нет, и это решение сервера: контракт объявляет
 * `X-Seeno-Step-Up` у `POST /invitations`, но не у этой операции. Спрашивать
 * пароль там, где сервер его не спросит, кабинет не станет.
 */
export function createAgencyClient(
  body: AgencyClientCreate,
  key: string,
): Promise<AgencyClient> {
  return api.post<AgencyClient>('/agency/clients', body, { idempotencyKey: key });
}

/**
 * Выделить пространству часть купленной квоты.
 *
 * Квота покупается **на организацию** и распределяется между пространствами
 * (спека §5): агентство двигает вопросы между клиентами, не докупая тариф
 * каждому. Сумма выделенного не может превысить квоту организации — при
 * попытке сервер отвечает `409`, и это единственная защита, которой стоит
 * доверять: считать остаток на клиенте значит рано или поздно разойтись с базой.
 */
export function updateAllocation(
  workspaceId: string,
  tracked: number,
): Promise<WorkspaceAllocation> {
  return api.put<WorkspaceAllocation>(
    `/workspaces/${encodeURIComponent(workspaceId)}/allocation`,
    { tracked_questions: tracked },
  );
}

export type Report = components['schemas']['Report'];

/**
 * Отчёт для клиента агентства. `kind: 'agency'` — это отчёт под брендом
 * агентства; на тарифах ниже Масштаба брендирование недоступно, и сервер
 * ответит `402`.
 */
export function createAgencyReport(
  workspaceId: string,
  brandId: string | null,
  periodStart: string,
  periodEnd: string,
  key: string,
): Promise<Report> {
  return api.post<Report>(
    '/reports',
    {
      kind: 'agency',
      // `pdf` сборка не верстает — заказ уходил в очередь и молча падал.
      format: 'html',
      workspace_id: workspaceId,
      brand_id: brandId,
      period_start: periodStart,
      period_end: periodEnd,
    },
    { idempotencyKey: key },
  );
}
